import {
  InfiniteData,
  infiniteQueryOptions,
  queryOptions,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {STORE_QUERY_KEY_MAP} from '../api/store/constant';
import {getStoreCommunityBestList} from '../api/store/getStoreCommunityBestList';
import {getStoreCommunityBlockList} from '../api/store/getStoreCommunityBlockList';
import {getStoreCommunityMycommentlist} from '../api/store/getStoreCommunityMycommentlist';
import {getStoreMyNews} from '../api/store/getStoreMyNews';
import {getStorePopup} from '../api/store/getStorePopup';
import {getStorePopupWelcome} from '../api/store/getStorePopupWelcome';
import {getStoreTodayGiftList} from '../api/store/getStoreTodayGiftList';
import {getStoreTvPointTermsCheck} from '../api/store/getStoreTvPointTermsCheck';
import {postStoreCashHistoryRetention} from '../api/store/postStoreCashHistoryRetention';
import {postStoreCashHistoryUsage} from '../api/store/postStoreCashHistoryUsage';
import {postStoreCommunityListComment} from '../api/store/postStoreCommunityListComment';
import {postStoreCommunityMyrating} from '../api/store/postStoreCommunityMyrating';
import {postStoreCouponHistoryBuy} from '../api/store/postStoreCouponHistoryBuy';
import {postStoreCouponHistoryDetails} from '../api/store/postStoreCouponHistoryDetails';
import {postStoreCouponHistoryRefundInfo} from '../api/store/postStoreCouponHistoryRefundInfo';
import {postStoreCouponHistoryUse} from '../api/store/postStoreCouponHistoryUse';
import {postStoreGetSingle} from '../api/store/postStoreGetSingle';
import {postStoreReceipt} from '../api/store/postStoreReceipt';
import {postStoreReceiptInfo} from '../api/store/postStoreReceiptInfo';
import {postStoreViewCash} from '../api/store/postStoreViewCash';
import type {FetcherOptions} from '../axios/axiosBffFetch';
import {requestContext} from '../common/requestContext';
import type {KakaoCashReportDataVo} from '../common/shared/api/nicolas/vo/KakaoCashReportDataVo.interface';
import type {SalesTicketVo} from '../common/shared/api/nicolas/vo/SalesTicketVo.interface';
import type {TicketSalesVo} from '../common/shared/api/nicolas/vo/TicketSalesVo.interface';
import {PagewebError} from '../errors/PagewebError.class';
import {createSimpleApiResponseError} from '../graphql/api/createSimpleApiResponseError';
import {createCommentItem} from '../graphql/factory/CommentItem';
import {createCommentList} from '../graphql/factory/CommentList';
import {createReceiptInfoResult} from '../graphql/factory/createReceiptInfoResult';
import {createExpiredCreditWithExpiredCredit} from '../graphql/factory/ExpiredCredit';
import {
  createHistoryWithKakaoCashReportDataVo,
  createHistoryWithSalesTicketVo,
  createHistoryWithTicketSalesVo,
} from '../graphql/factory/History';
import {createHistoryLineBannerWithHistoryLineBanner} from '../graphql/factory/HistoryLineBanner';
import {createMyNewsList} from '../graphql/factory/MyNewsList';
import {createMyRating} from '../graphql/factory/MyRating';
import {createPopup} from '../graphql/factory/popup';
import {createReceiptInfo} from '../graphql/factory/ReceiptInfo';
import {createSinglePreview} from '../graphql/factory/SinglePreview';
import {createTicketDetail} from '../graphql/factory/TicketDetail';
import {createTicketInSeries} from '../graphql/factory/TicketInSeries';
import {createTodayGiftList} from '../graphql/factory/TodayGiftList';
import {createUserCash} from '../graphql/factory/UserCash';
import {createWelcome} from '../graphql/factory/Welcome';
import {
  type BestCommentListQuery,
  type BestCommentListQueryVariables,
  BlockUserListQuery,
  type BlockUserListQueryVariables,
  type BuyCashHistoryQuery,
  type BuyCashHistoryQueryVariables,
  type BuyTicketHistoryQuery,
  type BuyTicketHistoryQueryVariables,
  CommentItemQuery,
  type CommentItemQueryVariables,
  type CommentListQuery,
  type CommentListQueryVariables,
  ContentTicketDetailQuery,
  type ContentTicketDetailQueryVariables,
  type ContentTicketInfoQuery,
  type ContentTicketInfoQueryVariables,
  HistoryType,
  type MyCommentListQuery,
  type MyCommentListQueryVariables,
  MyNewsQuery,
  type MyNewsQueryVariables,
  MyRatingQuery,
  type MyRatingQueryVariables,
  PopupQuery,
  type PopupQueryVariables,
  ReceiptInfoQuery,
  type ReceiptInfoQueryVariables,
  ReceiptInfoResultQuery,
  type ReceiptInfoResultQueryVariables,
  type ReplyCommentListQuery,
  type ReplyCommentListQueryVariables,
  SinglePreviewQuery,
  SinglePreviewQueryVariables,
  TodayGiftListQuery,
  type TodayGiftListQueryVariables,
  TvPointAgreementQuery,
  type TvPointAgreementQueryVariables,
  type UseCashHistoryQuery,
  type UseCashHistoryQueryVariables,
  UserCashQuery,
  type UserCashQueryVariables,
  type UseTicketHistoryQuery,
  type UseTicketHistoryQueryVariables,
  WelcomeQuery,
  type WelcomeQueryVariables,
} from '../graphql/generated/types';

/**
 * Store 도메인 Factory
 *
 * Adapter의 combine 로직을 queryFn에 통합하여 2-layer 아키텍처 구현
 */
export const storeFactory = {
  /**
   * 현금영수증 발행정보 조회
   */
  receiptInfoResult: (
    variables: ReceiptInfoResultQueryVariables,
    options?: Omit<
      UseQueryOptions<
        ReceiptInfoResultQuery,
        PagewebError,
        ReceiptInfoResultQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: STORE_QUERY_KEY_MAP.receiptInfoResult(variables),
      queryFn: async () => {
        const response = await postStoreReceipt(
          {
            creditMasterId: String(variables.creditMasterId),
          },
          fetcherOptions
        );

        if (!response.data) {
          throw createSimpleApiResponseError(
            '현금영수증 발행정보 데이터를 찾을 수 없습니다.'
          );
        }

        return {
          receiptInfoResult: createReceiptInfoResult(response.data),
        };
      },
      ...options,
    }),

  /**
   * 베스트 댓글 목록 조회 (Infinite)
   */
  infiniteBestCommentList: (
    variables: BestCommentListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        BestCommentListQuery,
        PagewebError,
        InfiniteData<BestCommentListQuery, BestCommentListQueryVariables>,
        BestCommentListQuery,
        ReturnType<typeof STORE_QUERY_KEY_MAP.infiniteBestCommentList>,
        BestCommentListQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: STORE_QUERY_KEY_MAP.infiniteBestCommentList(variables),
      queryFn: async ({pageParam}) => {
        const page = pageParam?.bestCommentListInput?.page || 0;
        const {bestCommentListInput} = pageParam;

        const response = await getStoreCommunityBestList(
          {
            seriesId: bestCommentListInput.seriesId,
            productId: bestCommentListInput.productId ?? undefined,
            page,
            size: bestCommentListInput.size ?? undefined,
          },
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError(
            'Best Comment 데이터를 찾을 수 없습니다.'
          );
        }

        return {bestCommentList: createCommentList(response.result)};
      },
      ...options,
    }),

  /**
   * 캐시 충전 내역 조회 (Infinite)
   */
  infiniteBuyCashHistory: (
    variables: BuyCashHistoryQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        BuyCashHistoryQuery,
        PagewebError,
        InfiniteData<BuyCashHistoryQuery, BuyCashHistoryQueryVariables>,
        BuyCashHistoryQuery,
        ReturnType<typeof STORE_QUERY_KEY_MAP.infiniteBuyCashHistory>,
        BuyCashHistoryQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: STORE_QUERY_KEY_MAP.infiniteBuyCashHistory(variables),
      queryFn: async ({pageParam}) => {
        const {lastPageId, usePaging} = pageParam;

        const response = await postStoreCashHistoryRetention(
          {
            usePaging: usePaging ?? undefined,
            lastPageId,
          },
          fetcherOptions
        );

        if (!response) {
          throw createSimpleApiResponseError('캐시 충전 내역 가져오기 실패');
        }

        return {
          buyCashHistory: {
            expiredCredit: response.expired_credit
              ? createExpiredCreditWithExpiredCredit(response.expired_credit)
              : undefined,
            list:
              response.list?.data.map((history: KakaoCashReportDataVo) =>
                createHistoryWithKakaoCashReportDataVo(history)
              ) || [],
            paging: {
              lastPageId: response.list?.paging?.last_page_id
                ? String(response.list?.paging?.last_page_id)
                : undefined,
              shouldReloadPage:
                !!lastPageId && response.list?.paging?.first_page,
            },
            banner: response.banner
              ? createHistoryLineBannerWithHistoryLineBanner(response.banner)
              : undefined,
          },
        };
      },
      ...options,
    }),

  /**
   * 이용권 충전 내역 조회 (Infinite)
   */
  infiniteBuyTicketHistory: (
    variables: BuyTicketHistoryQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        BuyTicketHistoryQuery,
        PagewebError,
        InfiniteData<BuyTicketHistoryQuery, BuyTicketHistoryQueryVariables>,
        BuyTicketHistoryQuery,
        ReturnType<typeof STORE_QUERY_KEY_MAP.infiniteBuyTicketHistory>,
        BuyTicketHistoryQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: STORE_QUERY_KEY_MAP.infiniteBuyTicketHistory(variables),
      queryFn: async ({pageParam}): Promise<BuyTicketHistoryQuery> => {
        const page = pageParam?.page ?? 0;
        const freeOnly = pageParam?.freeOnly ?? variables?.freeOnly;

        const response = await postStoreCouponHistoryBuy(
          {
            page,
            freeOnly: freeOnly ?? undefined,
          },
          fetcherOptions
        );

        if (!response) {
          throw createSimpleApiResponseError('이용권 충전 내역 가져오기 실패');
        }

        if (!response?.SalesTicketList) {
          return {
            buyTicketHistory: {
              list: [],
              isEnd: false,
            },
          };
        }

        return {
          buyTicketHistory: {
            list: response.SalesTicketList.content.map(
              (history: SalesTicketVo) =>
                createHistoryWithSalesTicketVo(
                  history,
                  HistoryType.CouponReCharge
                )
            ),
            isEnd: response.SalesTicketList.last,
          },
        };
      },
      ...options,
    }),

  /**
   * 사용자 캐시 정보 조회
   */
  userCash: (
    variables?: UserCashQueryVariables,
    options?: Omit<
      UseQueryOptions<UserCashQuery, PagewebError, UserCashQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: STORE_QUERY_KEY_MAP.userCash(variables),
      queryFn: async () => {
        const response = await postStoreViewCash(fetcherOptions);

        if (!response) {
          throw createSimpleApiResponseError('캐시 정보 가져오기 실패');
        }

        return {userCash: createUserCash(response)};
      },
      ...options,
    }),

  /**
   * 웰컴 팝업 정보 조회
   */
  welcome: (
    variables?: WelcomeQueryVariables,
    options?: Omit<
      UseQueryOptions<WelcomeQuery, PagewebError, WelcomeQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: STORE_QUERY_KEY_MAP.welcome(variables),
      queryFn: async () => {
        const response = await getStorePopupWelcome(fetcherOptions);

        return {welcome: createWelcome(response.result)};
      },
      ...options,
    }),

  /**
   * 오늘의 선물 목록 조회
   */
  todayGiftList: (
    variables?: TodayGiftListQueryVariables,
    options?: Omit<
      UseQueryOptions<TodayGiftListQuery, PagewebError, TodayGiftListQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: STORE_QUERY_KEY_MAP.todayGiftList(variables),
      queryFn: async () => {
        const response = await getStoreTodayGiftList(variables, fetcherOptions);

        if (!response?.result) {
          throw createSimpleApiResponseError('이용권 사용 내역 가져오기 실패');
        }

        const userInfo = requestContext.requestInfo?.user ?? null;

        return {todayGiftList: createTodayGiftList(userInfo, response.result)};
      },
      ...options,
    }),

  /**
   * 차단 사용자 목록 조회
   */
  blockUserList: (
    variables?: BlockUserListQueryVariables,
    options?: Omit<
      UseQueryOptions<BlockUserListQuery, PagewebError, BlockUserListQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: STORE_QUERY_KEY_MAP.blockUserList(variables),
      queryFn: async () => {
        const response = await getStoreCommunityBlockList(fetcherOptions);

        return {
          blockUserList: {
            list: response.result?.list || [],
          },
        };
      },
      ...options,
    }),

  /**
   * 댓글 단일 항목 조회
   */
  commentItem: (
    variables: CommentItemQueryVariables,
    options?: Omit<
      UseQueryOptions<CommentItemQuery, PagewebError, CommentItemQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: STORE_QUERY_KEY_MAP.commentItem(variables),
      queryFn: async () => {
        const response = await postStoreCommunityListComment(
          {
            page: 0,
            parentUid: variables.parentUid,
            seriesId: variables.seriesId,
          },
          fetcherOptions
        );

        if (!response || !response.comment_list?.[0]) {
          throw createSimpleApiResponseError('Comment Item이 없습니다.');
        }

        return {commentItem: createCommentItem(response.comment_list[0])};
      },
      ...options,
    }),

  /**
   * 댓글 목록 조회 (Infinite)
   */
  infiniteCommentList: (
    variables: CommentListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        CommentListQuery,
        PagewebError,
        InfiniteData<CommentListQuery, CommentListQueryVariables>,
        CommentListQuery,
        ReturnType<typeof STORE_QUERY_KEY_MAP.infiniteCommentList>,
        CommentListQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: STORE_QUERY_KEY_MAP.infiniteCommentList(variables),
      queryFn: async ({pageParam}): Promise<CommentListQuery> => {
        const response = await postStoreCommunityListComment(
          pageParam.commentListInput,
          fetcherOptions
        );

        return {
          commentList: createCommentList(response),
        };
      },
      ...options,
    }),

  /**
   * 회차 프리뷰 조회
   */
  singlePreview: (
    variables: SinglePreviewQueryVariables,
    options?: Omit<
      UseQueryOptions<SinglePreviewQuery, PagewebError, SinglePreviewQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: STORE_QUERY_KEY_MAP.singlePreview(variables),
      queryFn: async () => {
        const response = await postStoreGetSingle(
          {
            productId: variables.productId,
          },
          fetcherOptions
        );

        return {singlePreview: createSinglePreview(response)};
      },
      ...options,
    }),

  /**
   * 이용권 상세 정보 조회
   */
  contentTicketDetail: (
    variables: ContentTicketDetailQueryVariables,
    options?: Omit<
      UseQueryOptions<
        ContentTicketDetailQuery,
        PagewebError,
        ContentTicketDetailQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: STORE_QUERY_KEY_MAP.contentTicketDetail(variables),
      queryFn: async () => {
        const response = await postStoreCouponHistoryDetails(
          {
            ticketUid: variables.ticketUid,
            seriesId: variables.seriesId,
          },
          fetcherOptions
        );

        return {contentTicketDetail: createTicketDetail(response)};
      },
      ...options,
    }),

  /**
   * 콘텐츠 이용권 정보 조회 (Infinite)
   */
  infiniteContentTicketInfo: (
    variables: ContentTicketInfoQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        ContentTicketInfoQuery,
        PagewebError,
        InfiniteData<ContentTicketInfoQuery, ContentTicketInfoQueryVariables>,
        ContentTicketInfoQuery,
        ReturnType<typeof STORE_QUERY_KEY_MAP.infiniteContentTicketInfo>,
        ContentTicketInfoQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: STORE_QUERY_KEY_MAP.infiniteContentTicketInfo(variables),
      queryFn: async ({pageParam}): Promise<ContentTicketInfoQuery> => {
        const page = pageParam?.page ?? 0;
        const seriesId = pageParam.seriesId;

        const response = await postStoreCouponHistoryRefundInfo(
          {
            seriesId,
            page,
          },
          fetcherOptions
        );

        return {
          contentTicketInfo: createTicketInSeries(response),
        };
      },
      ...options,
    }),

  /**
   * 내 댓글 목록 조회 (Infinite)
   */
  infiniteMyCommentList: (
    variables: MyCommentListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        MyCommentListQuery,
        PagewebError,
        InfiniteData<MyCommentListQuery, MyCommentListQueryVariables>,
        MyCommentListQuery,
        ReturnType<typeof STORE_QUERY_KEY_MAP.infiniteMyCommentList>,
        MyCommentListQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: STORE_QUERY_KEY_MAP.infiniteMyCommentList(variables),
      queryFn: async ({pageParam}): Promise<MyCommentListQuery> => {
        const page = pageParam?.page ?? 0;

        const response = await getStoreCommunityMycommentlist(
          page,
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError(
            'My Comment 데이터를 찾을 수 없습니다.'
          );
        }

        return {
          myCommentList: createCommentList(response.result),
        };
      },
      ...options,
    }),

  /**
   * 답글 목록 조회 (Infinite)
   */
  infiniteReplyCommentList: (
    variables: ReplyCommentListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        ReplyCommentListQuery,
        PagewebError,
        InfiniteData<ReplyCommentListQuery, ReplyCommentListQueryVariables>,
        ReplyCommentListQuery,
        ReturnType<typeof STORE_QUERY_KEY_MAP.infiniteReplyCommentList>,
        ReplyCommentListQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: STORE_QUERY_KEY_MAP.infiniteReplyCommentList(variables),
      queryFn: async ({pageParam}): Promise<ReplyCommentListQuery> => {
        const page = pageParam?.commentListInput?.page ?? 0;

        const response = await postStoreCommunityListComment(
          {
            ...pageParam.commentListInput,
            size: 30,
            page,
          },
          fetcherOptions
        );

        return {
          replyCommentList: {
            ...createCommentList(response),
            commentList: response.comment_list
              .filter(item => !!item.parent_uid)
              .map(createCommentItem),
          },
        };
      },
      ...options,
    }),

  /**
   * 캐시 사용 내역 조회 (Infinite)
   */
  infiniteUseCashHistory: (
    variables: UseCashHistoryQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        UseCashHistoryQuery,
        PagewebError,
        InfiniteData<UseCashHistoryQuery, UseCashHistoryQueryVariables>,
        UseCashHistoryQuery,
        ReturnType<typeof STORE_QUERY_KEY_MAP.infiniteUseCashHistory>,
        UseCashHistoryQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: STORE_QUERY_KEY_MAP.infiniteUseCashHistory(variables),
      queryFn: async ({pageParam}): Promise<UseCashHistoryQuery> => {
        const page = pageParam?.page ?? 0;

        const response = await postStoreCashHistoryUsage(
          {
            page,
          },
          fetcherOptions
        );

        if (!response) {
          throw createSimpleApiResponseError('캐시 사용 내역 가져오기 실패');
        }

        return {
          useCashHistory: {
            list: response.cash_usage_list.content.map(
              (history: SalesTicketVo) =>
                createHistoryWithSalesTicketVo(history, HistoryType.CashUsage)
            ),
            isEnd: response.cash_usage_list.last,
          },
        };
      },
      ...options,
    }),

  /**
   * 이용권 사용 내역 조회 (Infinite)
   */
  infiniteUseTicketHistory: (
    variables: UseTicketHistoryQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        UseTicketHistoryQuery,
        PagewebError,
        InfiniteData<UseTicketHistoryQuery, UseTicketHistoryQueryVariables>,
        UseTicketHistoryQuery,
        ReturnType<typeof STORE_QUERY_KEY_MAP.infiniteUseTicketHistory>,
        UseTicketHistoryQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: STORE_QUERY_KEY_MAP.infiniteUseTicketHistory(variables),
      queryFn: async ({pageParam}): Promise<UseTicketHistoryQuery> => {
        const page = pageParam?.page ?? 0;
        const freeOnly = pageParam?.freeOnly ?? variables?.freeOnly;

        const response = await postStoreCouponHistoryUse(
          {
            page,
            freeOnly,
          },
          fetcherOptions
        );

        if (!response) {
          throw createSimpleApiResponseError('이용권 사용 내역 가져오기 실패');
        }

        if (!response?.TicketSalesList) {
          return {
            useTicketHistory: {
              list: [],
              isEnd: false,
            },
          };
        }

        return {
          useTicketHistory: {
            list: response.TicketSalesList.content.map(
              (history: TicketSalesVo) =>
                createHistoryWithTicketSalesVo(history)
            ),
            isEnd: response.TicketSalesList.last,
          },
        };
      },
      ...options,
    }),

  /**
   * 내 소식 목록 조회
   */
  myNews: (
    variables: MyNewsQueryVariables,
    options?: Omit<
      UseQueryOptions<MyNewsQuery, PagewebError, MyNewsQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: STORE_QUERY_KEY_MAP.myNews(variables),
      queryFn: async () => {
        const {tab, refresh} = variables.myNewsListInput;
        const response = await getStoreMyNews(
          {
            tab,
            refresh,
          },
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError(
            '알림 목록 정보를 찾을 수 없습니다.'
          );
        }

        return {
          myNewsList: createMyNewsList({
            news: response.result.news,
            display_ad: response.result.display_ad,
          }),
        };
      },
      ...options,
    }),

  /**
   * 내 평점 조회
   */
  myRating: (
    variables: MyRatingQueryVariables,
    options?: Omit<
      UseQueryOptions<MyRatingQuery, PagewebError, MyRatingQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: STORE_QUERY_KEY_MAP.myRating(variables),
      queryFn: async () => {
        const response = await postStoreCommunityMyrating(
          {
            seriesId: variables.seriesId,
            productId: variables.productId,
          },
          fetcherOptions
        );

        return {
          myRating: createMyRating({
            my_rating: response.my_rating,
          }),
        };
      },
      ...options,
    }),

  /**
   * 팝업 정보 조회
   */
  popup: (
    variables?: PopupQueryVariables,
    options?: Omit<
      UseQueryOptions<PopupQuery, PagewebError, PopupQuery>,
      'queryKey' | 'queryFn'
    >
  ) =>
    queryOptions({
      queryKey: STORE_QUERY_KEY_MAP.popup(variables),
      queryFn: async () => {
        const authToken = requestContext.requestInfo.user;

        if (!authToken) {
          return {popup: undefined};
        }

        const response = await getStorePopup();

        return {popup: createPopup(response.result)};
      },
      ...options,
    }),

  /**
   * 현금 영수증 정보 조회
   */
  receiptInfo: (
    variables?: ReceiptInfoQueryVariables,
    options?: Omit<
      UseQueryOptions<ReceiptInfoQuery, PagewebError, ReceiptInfoQuery>,
      'queryKey' | 'queryFn'
    >
  ) =>
    queryOptions({
      queryKey: STORE_QUERY_KEY_MAP.receiptInfo(variables),
      queryFn: async () => {
        const response = await postStoreReceiptInfo();

        if (!response?.data) {
          return {
            receiptInfo: {
              accountId: undefined,
              receiptInfoType: undefined,
              receiptInfo: undefined,
            },
          };
        }

        return {receiptInfo: createReceiptInfo(response.data)};
      },
      ...options,
    }),

  /**
   * TVPoint 약관 동의 여부 조회
   */
  tvPointAgreement: (
    variables?: TvPointAgreementQueryVariables,
    options?: Omit<
      UseQueryOptions<
        TvPointAgreementQuery,
        PagewebError,
        TvPointAgreementQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: STORE_QUERY_KEY_MAP.tvPointAgreement(variables),
      queryFn: async () => {
        const response = await getStoreTvPointTermsCheck(fetcherOptions);

        // Transform: Inverted logic from resolver
        // request_agree === 'N' means no agreement needed (already agreed)
        // request_agree === 'Y' means agreement needed (not agreed)
        return {
          tvPointAgreement: {
            isAgree: response.request_agree === 'N',
          },
        };
      },
      ...options,
    }),
} as const;
